import { supabase } from './supabase';
import { storageObjectPath } from './storageObjectPath';

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

/**
 * Uploads an image to Supabase Storage (Web version)
 * @param uri - Local URI of the image file (blob: or data: URL)
 * @param bucket - Storage bucket name (default: 'maintenance-photos')
 * @param folder - Optional folder path within bucket
 * @returns Promise with upload result
 */
export async function uploadImage(
  uri: string,
  bucket: string = 'maintenance-photos',
  folder?: string
): Promise<ImageUploadResult> {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);

    // Better file extension detection
    let fileExtension = 'jpg';
    if (uri.startsWith('data:')) {
      // Handle data URLs
      const match = uri.match(/data:([^;]+);/);
      if (match && match[1]) {
        if (match[1].includes('png')) fileExtension = 'png';
        else if (match[1].includes('jpeg') || match[1].includes('jpg')) fileExtension = 'jpg';
        else if (match[1].includes('gif')) fileExtension = 'gif';
        else if (match[1].includes('webp')) fileExtension = 'webp';
      }
    } else {
      // Handle regular file paths
      const pathExtension = uri.split('.').pop()?.toLowerCase();
      if (pathExtension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(pathExtension)) {
        fileExtension = pathExtension;
      }
    }

    const fileName = `${timestamp}_${randomId}.${fileExtension}`;

    // Create storage path
    const storagePath = folder ? `${folder}/${fileName}` : fileName;

    // Web: Handle blob/file directly using fetch
    let arrayBuffer: ArrayBuffer;

    if (uri.startsWith('blob:') || uri.startsWith('data:')) {
      const response = await fetch(uri);
      arrayBuffer = await response.arrayBuffer();
    } else {
      // Fallback: try to fetch as URL
      const response = await fetch(uri);
      arrayBuffer = await response.arrayBuffer();
    }

    // Upload to Supabase Storage using ArrayBuffer
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(storagePath, arrayBuffer, {
        contentType: `image/${fileExtension}`,
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    // Get public URL
    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(storagePath);

    return {
      success: true,
      url: ['maintenance-photos', 'profile-pictures'].includes(bucket)
        ? publicUrlData.publicUrl.replace('/object/public/', '/object/authenticated/') : publicUrlData.publicUrl
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
}

/**
 * Uploads multiple images to Supabase Storage
 * @param uris - Array of local URIs
 * @param bucket - Storage bucket name
 * @param folder - Optional folder path
 * @returns Promise with array of upload results
 */
export async function uploadMultipleImages(
  uris: string[],
  bucket: string = 'maintenance-photos',
  folder?: string
): Promise<ImageUploadResult[]> {
  const uploadPromises = uris.map(uri => uploadImage(uri, bucket, folder));
  return Promise.all(uploadPromises);
}

/**
 * Deletes an image from Supabase Storage
 * @param url - Public URL of the image
 * @param bucket - Storage bucket name
 */
export async function deleteImage(
  url: string,
  bucket: string = 'maintenance-photos'
): Promise<{ success: boolean; error?: string }> {
  try {
    const base = supabase.storage.from(bucket).getPublicUrl('').data.publicUrl;
    const fileName = storageObjectPath(url, bucket, base);
    if (bucket === 'profile-pictures') {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user || !fileName.startsWith(`${user.id}/`)) throw new Error('Access denied');
    }

    const { error } = await supabase.storage
      .from(bucket)
      .remove([fileName]);

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete image'
    };
  }
}

/**
 * Gets file info from URI (Web version)
 * @param uri - File URI
 * @returns File information including size
 */
export async function getFileInfo(uri: string): Promise<{
  size: number;
  name: string;
  type: string;
}> {
  const name = uri.split('/').pop() || 'image.jpg';
  const extension = name.split('.').pop() || 'jpg';

  try {
    if (uri.startsWith('blob:') || uri.startsWith('data:')) {
      const response = await fetch(uri);
      const blob = await response.blob();
      return {
        size: blob.size,
        name,
        type: blob.type || `image/${extension}`
      };
    } else {
      // For other URI types, return reasonable defaults
      console.log('Web: Using default file info for URI type:', uri.substring(0, 20) + '...');
      return {
        size: 0, // Size validation will be skipped
        name,
        type: `image/${extension}`
      };
    }
  } catch (error) {
    console.warn('Web: Error getting file info, using defaults:', error);
    return {
      size: 0,
      name,
      type: `image/${extension}`
    };
  }
}

/**
 * Validates image file (Web version)
 * @param uri - File URI
 * @param maxSizeBytes - Maximum file size in bytes (default: 8MB)
 * @returns Validation result
 */
export async function validateImage(
  uri: string,
  maxSizeBytes: number = 8 * 1024 * 1024 // 8MB
): Promise<{ valid: boolean; error?: string }> {
  try {
    console.log('Web: Validating image:', { uri: uri.substring(0, 50) + '...' });

    // Basic URI validation
    if (!uri) {
      return {
        valid: false,
        error: 'No image URI provided'
      };
    }

    // Validate file extension from URI
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const uriLower = uri.toLowerCase();
    const hasValidExtension = validExtensions.some(ext =>
      uriLower.includes(`.${ext}`) || uriLower.includes(`image/${ext}`)
    );

    if (!hasValidExtension && !uri.startsWith('blob:') && !uri.startsWith('data:image')) {
      return {
        valid: false,
        error: 'Invalid file type. Please select a valid image file (JPG, PNG, GIF, WebP)'
      };
    }

    // Try to get file info for size validation (if possible)
    try {
      const fileInfo = await getFileInfo(uri);
      if (fileInfo.size > 0 && fileInfo.size > maxSizeBytes) {
        return {
          valid: false,
          error: `File size (${(fileInfo.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size (${(maxSizeBytes / 1024 / 1024).toFixed(1)}MB)`
        };
      }
    } catch (fileInfoError) {
      console.log('Web: Could not get file size, skipping size validation');
    }

    console.log('Web: Image validation passed');
    return { valid: true };

  } catch (error: any) {
    console.error('Web: Image validation error:', error);
    return {
      valid: false,
      error: error.message || 'Failed to validate image'
    };
  }
}

/**
 * Uploads a PDF document to Supabase Storage (Web version)
 * @param file - File object from input element
 * @param bucket - Storage bucket name (default: 'documents')
 * @param folder - Optional folder path within bucket
 * @returns Promise with upload result
 */
export async function uploadDocument(
  file: File,
  bucket: string = 'documents',
  folder?: string
): Promise<ImageUploadResult> {
  try {
    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Invalid file type. Please upload a PDF or Word document.'
      };
    }

    // Validate file size (50MB max for documents)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size (50MB)`
      };
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    const fileName = `${timestamp}_${randomId}.${fileExtension}`;

    // Create storage path
    const storagePath = folder ? `${folder}/${fileName}` : fileName;

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(storagePath, arrayBuffer, {
        contentType: file.type,
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    // Get public URL (for documents bucket, we need signed URL since it's private)
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from(bucket)
      .createSignedUrl(storagePath, 60 * 60 * 24 * 365); // 1 year validity

    if (signedUrlError) {
      return {
        success: false,
        error: 'Document uploaded, but access could not be confirmed. The file was retained.'
      };
    }

    return {
      success: true,
      url: signedUrlData.signedUrl
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Document upload failed'
    };
  }
}

/**
 * Upload contract document specifically
 * @param file - PDF file from input
 * @param contractId - Contract ID for folder organization
 * @returns Upload result with URL
 */
export async function uploadContractDocument(
  file: File,
  contractId?: string
): Promise<ImageUploadResult> {
  const folder = contractId ? `contracts/${contractId}` : 'contracts';
  return uploadDocument(file, 'documents', folder);
}
